/**
  MIT License

  Copyright (c) 2017 olivierlsc

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
*/
import {isPlatformServer} from "@angular/common";
import {Inject, Injectable, PLATFORM_ID} from "@angular/core";
import {Subject, Observable} from "rxjs";

class HiddenKeyConstant {
  static DEFAULT = "hidden";
  static MS = "msHidden";
  static WEB_KIT = "webkitHidden";
}

class VisibilityStatusConstant {
  static VISIBLE = "visible";
  static HIDDEN = "hidden";
  static PRERENDER = "prerender";
  static UNLOADED = "unloaded";
}

enum AngularPageVisibilityStateEnum {
  VISIBLE,
  HIDDEN,
  PRERENDER,
  UNLOADED
}

/** Copy of `angular-page-visibility` > `AngularPageVisibilityService`. 
 * provides simple methods and observables for page visibility events. */
@Injectable({providedIn: "root"})
export class AngularPageVisibilityService {
  private onPageVisibleSource: Subject<void> = new Subject<void>();
  private onPageHiddenSource: Subject<void> = new Subject<void>();
  private onPagePrerenderSource: Subject<void> = new Subject<void>();
  private onPageUnloadedSource: Subject<void> = new Subject<void>();
  private onPageVisibilityChangeSource: Subject<AngularPageVisibilityStateEnum> = new Subject<AngularPageVisibilityStateEnum>();
  private hidden: string;
  private visibilityChange: string;
  private visibilityState: string;
  $onPageVisible: Observable<void> = this.onPageVisibleSource.asObservable();
  $onPageHidden: Observable<void> = this.onPageHiddenSource.asObservable();
  $onPagePrerender: Observable<void> = this.onPagePrerenderSource.asObservable();
  $onPageUnloaded: Observable<void> = this.onPageUnloadedSource.asObservable();
  $onPageVisibilityChange: Observable<AngularPageVisibilityStateEnum> = this.onPageVisibilityChangeSource.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.addEventListenerVibilityChange();
  }

  isPageVisible(): boolean {
    return VisibilityStatusConstant.VISIBLE === this.getVisibilityState() || !this.isHidden();
  }

  isPageHidden(): boolean {
    return VisibilityStatusConstant.HIDDEN === this.getVisibilityState() || this.isHidden();
  }

  isPagePrerender(): boolean {
    return VisibilityStatusConstant.PRERENDER === this.getVisibilityState();
  }

  isPageUnloaded(): boolean {
    return VisibilityStatusConstant.UNLOADED === this.getVisibilityState();
  }

  private isHidden(): boolean {
    return document[this.hidden];
  }

  private getVisibilityState(): string {
    return document[this.visibilityState];
  }

  private defineBrowserSupport() {
    if (typeof document[HiddenKeyConstant.DEFAULT] !== "undefined") {
      // Opera 12.10 and Firefox 18 and later support
      this.hidden = HiddenKeyConstant.DEFAULT;
      this.visibilityChange = "visibilitychange";
      this.visibilityState = "visibilityState";
    } else if (typeof document[HiddenKeyConstant.MS] !== "undefined") {
      this.hidden = HiddenKeyConstant.MS;
      this.visibilityChange = "msvisibilitychange";
      this.visibilityState = "msVisibilityState";
    } else if (typeof document[HiddenKeyConstant.WEB_KIT] !== "undefined") {
      this.hidden = HiddenKeyConstant.WEB_KIT;
      this.visibilityChange = "webkitvisibilitychange";
      this.visibilityState = "webkitVisibilityState";
    }
  }

  private addEventListenerVibilityChange(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.defineBrowserSupport();
    document.addEventListener(
      this.visibilityChange,
      () => {
        const vibilityState = this.getVisibilityState();
        switch (vibilityState) {
          case VisibilityStatusConstant.VISIBLE:
            this.onPageVisibilityChangeSource.next(
              AngularPageVisibilityStateEnum.VISIBLE
            );
            this.onPageVisibleSource.next();
            break;
          case VisibilityStatusConstant.HIDDEN:
            this.onPageVisibilityChangeSource.next(
              AngularPageVisibilityStateEnum.HIDDEN
            );
            this.onPageHiddenSource.next();
            break;
          case VisibilityStatusConstant.PRERENDER:
            this.onPageVisibilityChangeSource.next(
              AngularPageVisibilityStateEnum.PRERENDER
            );
            this.onPagePrerenderSource.next();
            break;
          case VisibilityStatusConstant.UNLOADED:
            this.onPageVisibilityChangeSource.next(
              AngularPageVisibilityStateEnum.UNLOADED
            );
            this.onPageUnloadedSource.next();
            break;
          default:
            if (this.isHidden()) {
              this.onPageVisibilityChangeSource.next(
                AngularPageVisibilityStateEnum.HIDDEN
              );
              this.onPageHiddenSource.next();
            } else {
              this.onPageVisibilityChangeSource.next(
                AngularPageVisibilityStateEnum.VISIBLE
              );
              this.onPageVisibleSource.next();
            }
        }
      },
      false
    );
  }
}